import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faBell } from "@fortawesome/free-regular-svg-icons";
import { useState, useEffect } from "react";
import { userNotificationOnOff } from "../services/userNotification";
import { Link, useNavigate } from "react-router-dom";
// import PageHeader from '../components/PageHeader'
import CustomPageHeader from "../components/pages/CustomPageHeader";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../components/ConfirmDialog";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import LanguageModal from "../components/Other/LanguageModal";
import {
  useFirebaseNotifications,
  useNotificationPermission,
} from "../hooks/useFirebaseNotifications";
import { toast } from "react-toastify";
import secureStorage from "../utils/secureStorage";

const Settings = () => {
  const { t } = useTranslation();
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deletePassword, setDeletePassword] = useState("");
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const navigate = useNavigate();
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  // Firebase notifications hooks
  const { requestPermission, clearToken } = useFirebaseNotifications();
  const { permission } = useNotificationPermission();

  useEffect(() => {
    // Set initial notification state based on browser permission
    setNotificationsEnabled(permission === "granted");
  }, [permission]);

  const toggleNotifications = async () => {
    try {
      if (!notificationsEnabled) {
        // User wants to enable notifications
        const granted = await requestPermission();
        if (granted) {
          setNotificationsEnabled(true);
          await userNotificationOnOff({ status: 1 }); // 1 for On
          // toast.success(t("notifications_enabled", "Notifications enabled"));
        } else {
          // Even if permission denied, still call API to update server state
          await userNotificationOnOff({ status: 1 }); // 1 for On
          // toast.error(
          //   t("notification_permission_denied", "Notification permission denied")
          // );
        }
      } else {
        // User wants to disable notifications
        setNotificationsEnabled(false);
        await userNotificationOnOff({ status: 2 }); // 2 for Off
        // toast.info(t("notifications_disabled", "Notifications disabled"));
      }
    } catch (error) {
      // Revert the state if API call fails
      setNotificationsEnabled(!notificationsEnabled);
    }
  };
  const { logout: authLogout, user } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  const confirmLogout = () => {
    // Clear Firebase tokens on logout
    clearToken();
    authLogout();
    setShowLogoutDialog(false);
    navigate("/login");
  };

  const handleDeleteAccount = async () => {
    // Require password confirmation for security
    if (!deletePassword || deletePassword.trim().length === 0) {
      setDeleteError(t("password_required_for_deletion", "Password is required to delete your account"));
      return;
    }
    
    setDeleting(true);
    setDeleteError("");
    try {
      const { deleteAccount } = await import("../api/user/index");
      await deleteAccount(deletePassword);
      
      // Clear all client-side data
      secureStorage.clear();
      // Clear Firebase tokens on account deletion
      clearToken();
      setDeleting(false);
      setShowDeleteModal(false);
      setDeletePassword("");
      navigate("/login");
    } catch (err) {
      setDeleteError(err.message || "Failed to delete account");
      setDeleting(false);
    }
  };

  const termsUrl = "https://vibelinksapp.com/terms-of-service/";
  const privacyUrl = "https://vibelinksapp.com/privacy-policy/";

  return (
    <>
      <CustomPageHeader
        hideBack={true}
        showPageHeader={false}
        pageContainerClass="px-2 pt-5 w-full mx-w-4xl"
        title={t("settings", "Vibelinks")}
      >
        <div>
          <h1 className="hidden lg:block  w-full  text-2xl font-bold text-gray-800 dark:text-dark-text mb-4">
            {t("settings", "Settings")}
          </h1>
          <div className="w-full  py-6 space-y-4 bg-white-200">
            {/* Profile card - keep large rounded */}
            <Link to="/profile" state={{ from: 'settings' }} className="block">
              <div className="bg-white dark:bg-[#252739] border border-gray-200 dark:border-[#1a1d35] shadow-sm rounded-xl p-4 flex items-center justify-between">
                <div className="flex items-center">
                  {(() => {
                    let user = null;
                    try {
                      user = secureStorage.getJSON("user");
                    } catch {}
                    return (
                      <>
                        <img
                          src={user?.image || "/vibe_newlogo.png"}
                          alt={
                            user?.full_name ||
                            user?.user_name ||
                            user?.email ||
                            "Profile"
                          }
                          className="h-8 w-8 min-h-12 min-w-12 rounded-full object-cover"
                        />
                        <div className="ml-3">
                          <p className="font-bold text-gray-800 dark:text-dark-text">
                            {user?.full_name ||
                              user?.user_name ||
                              user?.email ||
                              "User"}
                          </p>
                          <p className="text-gray-500 dark:text-dark-text-secondary text-sm">
                            {user?.email || ""}
                          </p>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <FontAwesomeIcon
                  icon={faChevronRight}
                  className="text-gray-400 dark:text-dark-text-secondary"
                />
              </div>
            </Link>

            {/* Dark Mode Toggle */}
            <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] p-4 flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-300 dark:text-dark-text-secondary" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
                <p className="ml-3 text-gray-700 dark:text-white font-medium">{t("dark_mode", "Dark Mode")}</p>
              </div>
              <div
                className={`w-14 h-7 rounded-full flex items-center p-1 cursor-pointer transition-all duration-300 ${
                  isDarkMode
                    ? "bg-gradient-to-r from-[#6d29f6] to-[#8b5cf6] justify-end"
                    : "bg-gray-300 dark:bg-white justify-start"
                }`}
                onClick={toggleTheme}
              >
                <div className="w-5 h-5 rounded-full shadow-md transition-transform" style={{ backgroundColor: 'white' }}></div>
              </div>
            </div>

            {/* Settings items - fully rounded */}
            <div className="space-y-3">
              <Link to="/profile" state={{ from: 'settings' }} className="block">
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  <MenuItem
                    icon={require("../images/app-icon/profile.png")}
                    title={t("profile_settings")}
                    hasArrow={true}
                  />
                </div>
              </Link>
              <Link to="/polls-list" state={{ from: 'settings' }} className="block">
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  <MenuItem
                    icon={require("../images/app-icon/poll_ios.png")}
                    title={t("polls")}
                    hasArrow={true}
                  />
                </div>
              </Link>
              {/* Notifications toggle - fully rounded */}
              <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] p-4 flex items-center justify-between">
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon={faBell}
                    className="text-gray-300 dark:text-dark-text-secondary w-5 h-5"
                  />
                  <p className="ml-3 text-gray-700 dark:text-dark-text">{t("notifications")}</p>
                </div>
                <div
                  className={`w-14 h-7 rounded-full flex items-center p-1 cursor-pointer transition-all duration-300 ${
                    notificationsEnabled
                      ? "bg-gradient-to-r from-[#6d29f6] to-[#8b5cf6] justify-end"
                      : "bg-gray-300 dark:bg-gray-600 justify-start"
                  }`}
                  onClick={toggleNotifications}
                >
                  <div className="w-5 h-5 rounded-full shadow-md transition-transform" style={{ backgroundColor: 'white' }}></div>
                </div>
              </div>

              {/* <Link to="/address" className="block">
                <div className="bg-gray-100 rounded-full overflow-hidden">
                  <MenuItem icon={faMapMarkerAlt} title="Manage Address" hasArrow={true} />
                </div>
              </Link> */}
              <Link to="/subscription" state={{ from: 'settings' }} className="block">
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  <MenuItem
                    icon={require("../images/app-icon/subscription.png")}
                    title={t("subscription")}
                    hasArrow={true}
                  />
                </div>
              </Link>
                {/* {user?.user_type !== 2 && ( */}
                <Link to="/change-password" state={{ from: 'settings' }} className="block">
                  <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                    <MenuItem
                      icon={require("../images/app-icon/change_password.png")}
                      title={t("change_password")}
                      hasArrow={true}
                    />
                  </div>
                </Link>
              {/* // )} */}
              <button
                onClick={() => setShowLanguageModal(true)}
                className="block w-full "
              >
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  <MenuItem
                    icon={require("../images/app-icon/change_languagee.png")}
                    title={t("change_language")}
                    hasArrow={true}
                  />
                </div>
              </button>

              <Link
                to="https://apps.apple.com/in/app/vibelinks/id6746448780"
                target="_blank"
                className="block"
              >
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  <MenuItem
                    icon={require("../images/app-icon/rate_grand.png")}
                    title={t("rate_us")}
                    hasArrow={true}
                  />
                </div>
              </Link>
              <Link
                to="https://vibelinksapp.com/#about"
                target="_blank"
                className="block"
              >
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  <MenuItem
                    icon={require("../images/app-icon/about.png")}
                    title={t("about_us")}
                    hasArrow={true}
                  />
                </div>
              </Link>
              <Link
                to="https://vibelinksapp.com/#contact"
                target="_blank"
                className="block"
              >
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  <MenuItem
                    icon={require("../images/app-icon/help_grand.png")}
                    title={t("help")}
                    hasArrow={true}
                  />
                </div>
              </Link>
              <Link to={privacyUrl} target="_blank" className="block">
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  <MenuItem
                    icon={require("../images/app-icon/privacy_grand.png")}
                    title={t("privacy_policy")}
                    hasArrow={true}
                  />
                </div>
              </Link>
              <Link to={termsUrl} target="_blank" className="block">
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  <MenuItem
                    icon={require("../images/app-icon/terms_grand.png")}
                    title={t("terms_of_use")}
                    hasArrow={true}
                  />
                </div>
              </Link>
              <Link
                to={"/"}
                className="block"
                onClick={(e) => {
                  e.preventDefault();
                  setShowLogoutDialog(true);
                }}
              >
                <div className="bg-white dark:bg-[#252739] rounded-xl shadow-sm border border-gray-200 dark:border-[#1a1d35] overflow-hidden">
                  {/* <img src="./logout_grand.png" /> */}
                  <MenuItem
                    icon={require("../images/app-icon/logout_grand.png")}
                    title={t("logout")}
                    hasArrow={true}
                  />
                </div>
              </Link>

              {/* <Link to="/stripe-connect" className="block">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <MenuItem icon={faCreditCard} title="Stripe Connect (Active)" hasArrow={true} />
                </div>
              </Link> */}

              {/* <Link to="/blocked-users" className="block">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <MenuItem icon={faUserSlash} title="Block User List" hasArrow={true} />
                </div>
              </Link> */}
            </div>

            {/* Info text below */}
            <div className="text-gray-800 dark:text-dark-text-secondary text-center text-sm space-y-0.5">
              <p>{t("version", "Version 8.0.0")}</p>
              <p>
                © 2025 {t("app_name", "VibeLinks")}{" "}
                {t("all_rights_reserved", "All Rights Reserved")}
              </p>
            </div>
            {/* Delete account button - fully rounded */}
            <button
              className="w-full bg-primaryColor text-white font-medium py-3 rounded-xl"
              onClick={() => setShowDeleteModal(true)}
            >
              {t("DELETE_ACCOUNT")}
            </button>

            {/* Delete Account Confirmation Modal */}
            {showDeleteModal && (
              <div className="fixed inset-0 z-[99999999] flex items-center justify-center bg-black bg-opacity-60 px-4">
                <div className="bg-white dark:bg-[#252739] shadow-2xl text-center relative border-white dark:border-[#1a1d35] border-2 rounded-xl p-6 w-full max-w-md">
                  {/* Logo overlapping the modal */}
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2">
                    <img
                      src="/logo-bg.png"
                      alt="VibeLinks"
                      className="h-16 w-16 sm:h-20 sm:w-20 object-contain"
                    />
                  </div>
                  {/* Modal container */}
                  <div className="pt-12">
                    <h2 className="text-black dark:text-dark-text text-xl font-bold mb-2">{t("app_name", "VibeLinks")}</h2>
                    <p className="text-black dark:text-dark-text mb-4">
                      {t(
                        "If_you_proceed_you_will_loose_your_all_personal_data"
                      )}
                      {t("Are_you_sure_you_want_to_delete_your_account")}
                    </p>
                    
                    {/* Password confirmation input */}
                    <div className="mb-4">
                      <label className="block text-left text-sm font-medium text-black dark:text-dark-text mb-2">
                        {t("enter_password_to_confirm", "Enter your password to confirm deletion")}
                      </label>
                      <div className="relative">
                        <input
                          type={showDeletePassword ? "text" : "password"}
                          value={deletePassword}
                          onChange={(e) => setDeletePassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#6d29f6] bg-white dark:bg-[#1a1d35] text-black dark:text-white"
                          placeholder={t("password", "Password")}
                          disabled={deleting}
                        />
                        <button
                          type="button"
                          onClick={() => setShowDeletePassword(!showDeletePassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400"
                        >
                          {showDeletePassword ? "👁️" : "👁️‍🗨️"}
                        </button>
                      </div>
                    </div>
                    
                    {deleteError && (
                      <p className="text-red-500 mb-4 text-sm">{deleteError}</p>
                    )}
                    <div className="flex justify-center gap-4">
                      <button
                        className="bg-[#e85a59] text-white rounded-lg font-bold py-2 px-8 text-lg transition hover:opacity-90"
                        onClick={() => {
                          setShowDeleteModal(false);
                          setDeletePassword("");
                          setDeleteError("");
                        }}
                        disabled={deleting}
                      >
                        {t("Cancel", "Cancel")}
                      </button>
                      <button
                        className="bg-[#6d2af6] text-white rounded-lg font-bold py-2 px-8 text-lg transition hover:opacity-90 disabled:opacity-50"
                        onClick={handleDeleteAccount}
                        disabled={deleting || !deletePassword}
                      >
                        {deleting ? t("Deleting...", "Deleting...") : t("Delete Account", "Delete Account")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </CustomPageHeader>

      <ConfirmDialog
        open={showLogoutDialog}
        title={t("Sidebar.logout_title")}
        message={t("Sidebar.logout_message")}
        onConfirm={confirmLogout}
        onCancel={() => {
          // toggle();
          setShowLogoutDialog(false);
        }}
        yesLabel={t("Sidebar.logout_yes")}
        noLabel={t("Sidebar.logout_no")}
      />

      <LanguageModal
        open={showLanguageModal}
        onClose={() => setShowLanguageModal(false)}
      />
    </>
  );
};

// MenuItem component with dark mode support
const MenuItem = ({ icon, title, hasArrow = false }) => (
  <div className="p-4 flex items-center justify-between shadow-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-[#151729]">
    <div className="flex items-center">
      {/* <FontAwesomeIcon icon={icon} className='text-gray-500 w-5 h-5' /> */}
      <img src={icon} alt={title} />
      <p className="ml-3 text-gray-700 dark:text-dark-text">{title}</p>
    </div>
    {hasArrow && (
      <FontAwesomeIcon icon={faChevronRight} className="text-gray-400 dark:text-dark-text-secondary" />
    )}
  </div>
);

export default Settings;
